import axios from 'axios';

// Store for loading functions - will be set by App.js
let loadingFunctions = {
  showLoading: null,
  hideLoading: null,
};

// Flag to prevent multiple interceptor registrations
let interceptorsSetup = false;

// List of endpoints that should NOT show the loader
const silentEndpoints = [
  '/api/admin/getNotificationListAdminById',
];

// Check if URL should be silent (no loader)
const isSilentRequest = (url) => {
  if (!url) return false;
  return silentEndpoints.some(endpoint => url.includes(endpoint));
};

// Handle force logout - clear storage and redirect to login
const handleForceLogout = (message) => {
  // Prevent multiple redirects
  if (window._isLoggingOut) return;
  window._isLoggingOut = true;

  // Clear all local storage
  localStorage.clear();

  // Store message to show on login page (non-blocking approach)
  if (message) {
    sessionStorage.setItem('logoutMessage', message);
  }

  // Redirect to login page using replace to prevent back navigation
  window.location.replace('/login');
};

// Setup function to initialize interceptors with loading context
export const setupAxiosInterceptors = (showLoading, hideLoading) => {
  loadingFunctions.showLoading = showLoading;
  loadingFunctions.hideLoading = hideLoading;

  // Only setup interceptors once
  if (interceptorsSetup) {
    return;
  }
  interceptorsSetup = true;

  // Request interceptor - show loader
  axios.interceptors.request.use(
    (config) => {
      // Mark this request with a unique ID for tracking
      config._loaderId = Date.now() + Math.random();

      // Check if this request should show loader
      if (!config.silent && !isSilentRequest(config.url)) {
        if (loadingFunctions.showLoading) {
          loadingFunctions.showLoading();
        }
        config._showedLoader = true;
      }
      return config;
    },
    (error) => {
      if (loadingFunctions.hideLoading) {
        loadingFunctions.hideLoading();
      }
      return Promise.reject(error);
    }
  );

  // Response interceptor - hide loader and handle force logout
  axios.interceptors.response.use(
    (response) => {
      // Only hide if we showed loader for this request
      if (response.config && response.config._showedLoader) {
        if (loadingFunctions.hideLoading) {
          loadingFunctions.hideLoading();
        }
      }

      // Check for force logout in response data
      if (response.data && response.data.forceLogout) {
        handleForceLogout(response.data.message);
        return Promise.reject(new Error(response.data.message));
      }

      return response;
    },
    (error) => {
      // Only hide if we showed loader for this request
      if (error.config && error.config._showedLoader) {
        if (loadingFunctions.hideLoading) {
          loadingFunctions.hideLoading();
        }
      }

      // Check for 401 status or force logout
      if (error.response) {
        const { status, data } = error.response;
        if (status === 401 || (data && data.forceLogout)) {
          handleForceLogout(data?.message || 'Session expired. Please login again.');
        }
      }

      return Promise.reject(error);
    }
  );
};

// Export for manual control if needed
export const showGlobalLoader = (message) => {
  if (loadingFunctions.showLoading) {
    loadingFunctions.showLoading(message);
  }
};

export const hideGlobalLoader = () => {
  if (loadingFunctions.hideLoading) {
    loadingFunctions.hideLoading();
  }
};

export default axios;
